/**
 * VirtualList Component
 * High-performance virtualized list for card-based layouts
 *
 * Features:
 * - Virtual scrolling for 10,000+ items at 60fps
 * - Variable height support
 * - Custom item rendering
 * - Grid layout option (multiple columns)
 * - Performance monitoring
 * - Responsive design
 */

import React, { useRef, useState, useEffect, useMemo, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Search } from 'lucide-react';
import '../styles/virtual-scroll.css';

// Props interface
export interface VirtualListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  estimateSize?: number | ((index: number) => number);
  height?: number | string;
  width?: number | string;
  overscan?: number;
  keyExtractor: (item: T, index: number) => string | number;
  emptyMessage?: string;
  showPerformanceMetrics?: boolean;
  className?: string;
  gap?: number;
  columns?: number; // For grid layout
  horizontal?: boolean;
}

// Performance metrics interface
interface PerformanceMetrics {
  fps: number;
  visibleItems: number;
  totalItems: number;
}

export function VirtualList<T>({
  items,
  renderItem,
  estimateSize = 180,
  height = 600,
  width = '100%',
  overscan = 10,
  keyExtractor,
  emptyMessage = 'No items available',
  showPerformanceMetrics = false,
  className = '',
  gap = 12,
  columns = 1,
  horizontal = false,
}: VirtualListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    visibleItems: 0,
    totalItems: items.length,
  });
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());

  // Calculate row count for grid layout
  const rowCount = useMemo(() => {
    if (columns <= 1) return items.length;
    return Math.ceil(items.length / columns);
  }, [items.length, columns]);

  // Get estimated size function
  const getEstimateSize = useMemo(() => {
    if (typeof estimateSize === 'function') return estimateSize;
    return () => estimateSize + gap;
  }, [estimateSize, gap]);

  // Virtual row configuration
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: getEstimateSize,
    overscan,
    horizontal,
  });

  // Performance monitoring
  useEffect(() => {
    if (!showPerformanceMetrics) return undefined;

    let animationFrameId: number;
    const measureFPS = (): void => {
      frameCount.current++;
      const currentTime = performance.now();
      const elapsed = currentTime - lastTime.current;

      if (elapsed >= 1000) {
        setMetrics({
          fps: Math.round((frameCount.current * 1000) / elapsed),
          visibleItems: rowVirtualizer.getVirtualItems().length * columns,
          totalItems: items.length,
        });
        frameCount.current = 0;
        lastTime.current = currentTime;
      }

      animationFrameId = requestAnimationFrame(measureFPS);
    };

    animationFrameId = requestAnimationFrame(measureFPS);
    return () => cancelAnimationFrame(animationFrameId);
  }, [showPerformanceMetrics, items.length, columns, rowVirtualizer]);

  // Empty state
  if (items.length === 0) {
    return (
      <div className={`virtual-table-container ${className}`}>
        <div className="virtual-scroll-empty">
          <Search className="virtual-scroll-empty-icon" />
          <div className="virtual-scroll-empty-text">{emptyMessage}</div>
        </div>
      </div>
    );
  }

  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <div className={className} style={{ position: 'relative' }}>
      <div
        ref={parentRef}
        className="virtual-list-container"
        style={{
          height: typeof height === 'number' ? `${height}px` : height,
          width: typeof width === 'number' ? `${width}px` : width,
        }}
      >
        <div
          style={{
            height: horizontal ? '100%' : `${rowVirtualizer.getTotalSize()}px`,
            width: horizontal ? `${rowVirtualizer.getTotalSize()}px` : '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map((virtualRow) => {
            // For grid layout, calculate items in this row
            const startIndex = columns > 1 ? virtualRow.index * columns : virtualRow.index;
            const rowItems = columns > 1
              ? items.slice(startIndex, startIndex + columns)
              : [items[virtualRow.index]];

            return (
              <div
                key={virtualRow.index}
                className="virtual-list-item"
                style={{
                  position: 'absolute',
                  top: horizontal ? 0 : 0,
                  left: horizontal ? 0 : 0,
                  width: horizontal ? 'auto' : '100%',
                  height: horizontal ? '100%' : 'auto',
                  transform: horizontal
                    ? `translateX(${virtualRow.start}px)`
                    : `translateY(${virtualRow.start}px)`,
                  padding: `${gap / 2}px`,
                }}
              >
                {columns > 1 ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${columns}, 1fr)`,
                      gap: `${gap}px`,
                    }}
                  >
                    {rowItems.map((item, colIndex) => {
                      const actualIndex = startIndex + colIndex;
                      if (!item) return null;
                      return (
                        <div key={keyExtractor(item, actualIndex)}>
                          {renderItem(item, actualIndex)}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  rowItems[0] && renderItem(rowItems[0], virtualRow.index)
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance Metrics */}
      {showPerformanceMetrics && (
        <div className={`virtual-scroll-perf visible ${metrics.fps < 30 ? 'danger' : metrics.fps < 50 ? 'warning' : ''}`}>
          {metrics.fps} FPS | {metrics.visibleItems}/{metrics.totalItems} items
        </div>
      )}
    </div>
  );
}

export default VirtualList;
